import { useState, useEffect, useRef } from 'react';
import { Edit2, ChevronUp, Trash2, EyeOff } from 'lucide-react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import FooterButtonsList from '../core/HeadersAndFooters/FooterButtonsList';
import ConfirmationModal from '../core/ConfirmationModal';
import StandardButton from '../core/StandardButton';
import AddEditArticle from '../news/AddEditArticle';
import AddEditEvent from '../events/AddEditEvent';
import FullScreenOverlay from '../core/FullScreenOverlay';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useTranslation } from '../../i18n/useTranslation';

// Import protected Supabase client and direct fetch helper from shared location
import { supabaseFetch } from '../../lib/supabase';
import { isPlatformAdmin } from '../../utils/adminHelpers';
import { getMyStewardships } from '../../data/directoryService';

// Auth token helper - uses supabaseFetch.getAuthToken() which auto-refreshes expired JWTs
const getAuthToken = () => supabaseFetch.getAuthToken();

const MyArticles = ({ userId, currentContext, onBack, onArticleClick, onEventClick, isVisiting = false }) => {
  // Use custom navigation events instead of react-router here
  const { isMobile } = useMediaQuery();
  const { t } = useTranslation('user');
  const [articles, setArticles] = useState([]);
  const [viewCounts, setViewCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [articleTypeFilter, setArticleTypeFilter] = useState('both');
  const [showGoToTop, setShowGoToTop] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ visible: false, article: null, commentCount: 0 });
  const [infoModal, setInfoModal] = useState({ visible: false, message: '' });
  const showInfo = (message) => setInfoModal({ visible: true, message });
  const [deleting, setDeleting] = useState(false);
  const [hideModal, setHideModal] = useState({ visible: false, article: null });
  const [hiding, setHiding] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [groupNames, setGroupNames] = useState({});    // { community_id: name }
  const [groupFilter, setGroupFilter] = useState('all');  // 'all' or a community_id
  const [sortBy, setSortBy] = useState('date_desc');      // 'date_desc', 'date_asc', 'name_asc', 'name_desc', 'group'
  const scrollContainerRef = useRef(null);
  const [editModal, setEditModal] = useState({ visible: false, article: null }); // Modal edit overlay
  const [refreshCounter, setRefreshCounter] = useState(0); // Increment to trigger article re-fetch
  const [showPendingEventsLink, setShowPendingEventsLink] = useState(false); // Steward/admin only — §6.4 second entry point

  // Steward/admin gate for the "Review pending events" link (mirrors ProfileMenu's showStewardEntry
  // pattern). `isAdmin` is the existing state this file already derives from the auth JWT above.
  useEffect(() => {
    if (!userId) { setShowPendingEventsLink(false); return; }
    if (isAdmin) { setShowPendingEventsLink(true); return; }
    getMyStewardships().then(rows => setShowPendingEventsLink(rows.length > 0));
  }, [userId, isAdmin]);

  // Logo click listener - navigate back to MainFeed
  useEffect(() => {
    const handleLogoClick = () => {
      if (onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack]);

  // Handle scroll for go-to-top button (try container first, fallback to window)
  useEffect(() => {
    if (!isMobile) return;

    const container = scrollContainerRef.current;

    const handleScroll = () => {
      // Check container scroll first, then window scroll
      const scrollTop = container?.scrollTop || window.scrollY || document.documentElement.scrollTop;
      setShowGoToTop(scrollTop > 150);
    };

    // Listen to both container and window scroll
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('scroll', handleScroll);

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  const scrollToTop = () => {
    // Try container scroll first, fallback to window
    if (scrollContainerRef.current && scrollContainerRef.current.scrollTop > 0) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const fetchUserArticles = async () => {
      try {
        setLoading(true);

        // Check if the logged-in viewer (not the userId prop, which may be someone else's
        // profile being viewed) is a platform admin — a self-row lookup by the JWT's own
        // subject, same pattern App.js already uses for the viewer's own profile fields.
        let adminUser = false;
        try {
          const authToken = await getAuthToken();
          if (authToken) {
            const payload = JSON.parse(atob(authToken.split('.')[1]));
            const { data: viewerProfile } = await supabaseFetch.query('users', {
              select: 'is_sys_admin',
              eq: { id: payload.sub },
              single: true
            });
            adminUser = isPlatformAdmin(viewerProfile);
          }
        } catch (e) {
          // Token decode or lookup failed — not admin
        }
        setIsAdmin(adminUser);

        // Fetch articles owned by current user
        const { data, error: fetchError } = await supabaseFetch.query('articles', {
          select: '*',
          eq: { author_id: userId },
          order: 'created_at.desc'
        });

        if (fetchError) throw fetchError;

        const allArticles = data || [];

        setArticles(allArticles);

        // Fetch group names for articles that belong to a community
        const communityIds = [...new Set(allArticles.map(a => a.community_id).filter(Boolean))];
        if (communityIds.length > 0) {
          const { data: communityData } = await supabaseFetch.query('communities', {
            select: 'id,name',
            inFilter: { id: communityIds }
          });
          if (communityData) {
            const names = {};
            communityData.forEach(c => { names[c.id] = c.name; });
            setGroupNames(names);
          }
        }

        // Fetch accurate view counts from article_engagement (same source as ArticleDetail)
        const articleIds = allArticles.map(a => a.id);
        if (articleIds.length > 0) {
          const { data: engagementData } = await supabaseFetch.query('article_engagement', {
            select: 'article_id',
            eq: { engagement_type: 'view' },
            inFilter: { article_id: articleIds }
          });
          if (engagementData) {
            const counts = {};
            engagementData.forEach(row => {
              counts[row.article_id] = (counts[row.article_id] || 0) + 1;
            });
            setViewCounts(counts);
          }
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching articles:', err);
        setError('Failed to load articles');
      } finally {
        setLoading(false);
      }
    };

    fetchUserArticles();
  }, [userId, refreshCounter]);

  const handleNewArticle = () => {
    window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'new-article', mode: 'add' } }));
  };

  const handleNewEvent = () => {
    window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'new-event', mode: 'add' } }));
  };

  const handleViewArticle = (article) => {
    if (article.article_type === 'event') {
      onEventClick?.(article.id);
    } else {
      onArticleClick?.(article.id);
    }
  };

  const handleEditArticle = (article) => {
    // Open edit form in modal overlay (preserves MyArticles state)
    setEditModal({ visible: true, article });
  };

  const handleEditModalClose = () => {
    setEditModal({ visible: false, article: null });
    // Refresh articles list to pick up any changes
    setRefreshCounter(prev => prev + 1);
  };

  const handleDeleteArticle = async (article) => {
    // Open immediately; the comment count fills in to scale the warning.
    setDeleteModal({ visible: true, article, commentCount: 0 });
    try {
      const { data } = await supabaseFetch.query('chats', {
        select: 'id,is_deleted_by_author',
        eq: { article_id: article.id }
      });
      const count = (data || []).filter(c => !c.is_deleted_by_author).length;
      setDeleteModal(prev =>
        prev.visible && prev.article?.id === article.id ? { ...prev, commentCount: count } : prev
      );
    } catch (e) {
      console.warn('Comment count for delete warning failed:', e);
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal.article) return;

    try {
      setDeleting(true);

      // Hard-delete via the SECURITY DEFINER RPC: it deletes the article's comment
      // rows (children-first, RLS-bypassed) then the article; message_engagement
      // cascades off chats. chats.article_id is a NO ACTION FK, so a bare
      // delete('articles') BLOCKS once comments exist — the RPC is the only correct
      // path. Events are stored as articles, so this covers events too.
      const { error: deleteError } = await supabaseFetch.rpc('delete_article_with_comments', {
        p_article_id: deleteModal.article.id
      });

      if (deleteError) throw deleteError;

      // Remove article from local state
      setArticles(prevArticles =>
        prevArticles.filter(a => a.id !== deleteModal.article.id)
      );

      // Close modal
      setDeleteModal({ visible: false, article: null });

    } catch (err) {
      console.error('Error deleting article:', err);
      showInfo(t('delete_error') || 'Failed to delete article. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ visible: false, article: null });
  };

  const handleHideArticle = (article) => {
    setHideModal({ visible: true, article });
  };

  const confirmHide = async () => {
    if (!hideModal.article) return;

    try {
      setHiding(true);
      const authToken = await getAuthToken();

      const { error: hideError } = await supabaseFetch.update('articles',
        { is_published: false },  // the date stays: a post's date is its first publication (#78 addendum 5)
        { eq: { id: hideModal.article.id }, authToken }
      );

      if (hideError) throw hideError;

      // Update local state — mark as unpublished
      setArticles(prevArticles =>
        prevArticles.map(a =>
          a.id === hideModal.article.id
            ? { ...a, is_published: false }
            : a
        )
      );

      setHideModal({ visible: false, article: null });
      window.dispatchEvent(new CustomEvent('ournet:articlesChanged'));
    } catch (err) {
      console.error('Error hiding article:', err);
      showInfo(t('hide_error') || 'Failed to hide article. Please try again.');
    } finally {
      setHiding(false);
    }
  };

  const cancelHide = () => {
    setHideModal({ visible: false, article: null });
  };

  const formatDate = (dateString) => {
    // Return empty string for null/undefined/empty inputs
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    // If invalid date, return empty string
    if (isNaN(date.getTime())) return 'N/A';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#EDF2F8',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header - Layered architecture (responsive handled internally) */}
      <HeaderContainer />
      <HeaderButtonsForm
        currentContext={currentContext}
        onBack={onBack}
        headerTitle={t('my_articles_title')}
      />

      {/* Main Content */}
      <div
        ref={scrollContainerRef}
        style={{
          marginTop: isMobile ? '50px' : '80px',
          marginBottom: isMobile ? '0' : '120px',
          paddingTop: isMobile ? '8px' : '12px',
          paddingBottom: isMobile ? '70px' : '20px',
          paddingLeft: isMobile ? '8px' : '20px',
          paddingRight: isMobile ? '8px' : '20px',
          flex: 1,
          overflowY: 'auto'
        }}
      >
        {/* Pending Events link — steward/admin only, §6.4 second entry point */}
        {showPendingEventsLink && (
          <div style={{ marginBottom: isMobile ? '10px' : '8px', maxWidth: isMobile ? '100%' : '1000px' }}>
            <StandardButton
              variant="utility"
              label={t('pending_events_link')}
              onClick={() => window.dispatchEvent(new CustomEvent('ournet:navigate', { detail: { view: 'events-pending' } }))}
            />
          </div>
        )}

        {/* Filter and Buttons Row */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          marginBottom: isMobile ? '10px' : '8px',
          gap: isMobile ? '8px' : '12px',
          maxWidth: isMobile ? '100%' : '1000px'
        }}>
          {/* Article Type Filter */}
          <div style={{
            flex: isMobile ? 'none' : 1,
            display: 'flex',
            justifyContent: isMobile ? 'center' : 'flex-start',
            gap: isMobile ? '12px' : '24px',
            background: isMobile ? 'white' : 'transparent',
            padding: isMobile ? '8px' : '0',
            borderRadius: isMobile ? '6px' : '0',
            boxShadow: isMobile ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              cursor: 'pointer',
              fontSize: isMobile ? '13px' : '14px',
              fontFamily: 'OpenSauceSans',
              fontWeight: '600',
              color: '#1a1a1a'
            }}>
              <input
                type="radio"
                name="articleType"
                value="news"
                checked={articleTypeFilter === 'news'}
                onChange={(e) => setArticleTypeFilter(e.target.value)}
                style={{
                  width: isMobile ? '16px' : '16px',
                  height: isMobile ? '16px' : '16px',
                  cursor: 'pointer'
                }}
              />
              {t('filter_news')}
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              cursor: 'pointer',
              fontSize: isMobile ? '13px' : '14px',
              fontFamily: 'OpenSauceSans',
              fontWeight: '600',
              color: '#1a1a1a'
            }}>
              <input
                type="radio"
                name="articleType"
                value="event"
                checked={articleTypeFilter === 'event'}
                onChange={(e) => setArticleTypeFilter(e.target.value)}
                style={{
                  width: isMobile ? '16px' : '16px',
                  height: isMobile ? '16px' : '16px',
                  cursor: 'pointer'
                }}
              />
              {t('filter_event')}
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              cursor: 'pointer',
              fontSize: isMobile ? '13px' : '14px',
              fontFamily: 'OpenSauceSans',
              fontWeight: '600',
              color: '#1a1a1a'
            }}>
              <input
                type="radio"
                name="articleType"
                value="both"
                checked={articleTypeFilter === 'both'}
                onChange={(e) => setArticleTypeFilter(e.target.value)}
                style={{
                  width: isMobile ? '16px' : '16px',
                  height: isMobile ? '16px' : '16px',
                  cursor: 'pointer'
                }}
              />
              {t('filter_both')}
            </label>
          </div>

          {/* Buttons — hidden during an active visit (Change 3): article/event creation is not
              available while visiting, and this page has no context restriction to piggyback a
              guard on (unlike the front-page "+" buttons), so it needs its own explicit check. */}
          {!isVisiting && (
          <div style={{
            display: 'flex',
            gap: isMobile ? '6px' : '8px'
          }}>
            <button
              onClick={handleNewArticle}
              style={{
                flex: isMobile ? 1 : 'none',
                background: '#5BC0BE',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: isMobile ? '10px 12px' : '8px 16px',
                fontSize: isMobile ? '13px' : '13px',
                fontFamily: 'OpenSauceSans',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#4AA9A7'}
              onMouseLeave={(e) => e.target.style.background = '#5BC0BE'}
            >
              {t('button_new_article')}
            </button>

            <button
              onClick={handleNewEvent}
              style={{
                flex: isMobile ? 1 : 'none',
                background: '#5BC0BE',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: isMobile ? '10px 12px' : '8px 16px',
                fontSize: isMobile ? '13px' : '13px',
                fontFamily: 'OpenSauceSans',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#4AA9A7'}
              onMouseLeave={(e) => e.target.style.background = '#5BC0BE'}
            >
              {t('button_new_event')}
            </button>
          </div>
          )}
        </div>

        {/* Group Filter + Sort Row */}
        {!loading && articles.length > 0 && (
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            marginBottom: isMobile ? '10px' : '8px',
            gap: isMobile ? '8px' : '12px',
            maxWidth: isMobile ? '100%' : '1000px'
          }}>
            {/* Group Dropdown */}
            <div style={{
              flex: isMobile ? 'none' : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <label style={{
                fontSize: isMobile ? '12px' : '13px',
                fontFamily: 'OpenSauceSans',
                fontWeight: '600',
                color: '#666',
                whiteSpace: 'nowrap'
              }}>
                {t('filter_group') || 'Group'}:
              </label>
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                style={{
                  flex: 1,
                  padding: isMobile ? '8px 10px' : '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: isMobile ? '13px' : '13px',
                  fontFamily: 'OpenSauceSans',
                  fontWeight: '500',
                  color: '#1a1a1a',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="all">{t('filter_all_groups') || 'All Groups'}</option>
                <option value="none">{t('filter_no_group') || 'No Group (Locality)'}</option>
                {Object.entries(groupNames)
                  .sort(([, a], [, b]) => a.localeCompare(b))
                  .map(([id, name]) => (
                    <option key={id} value={id}>{name}</option>
                  ))
                }
              </select>
            </div>

            {/* Sort Dropdown */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <label style={{
                fontSize: isMobile ? '12px' : '13px',
                fontFamily: 'OpenSauceSans',
                fontWeight: '600',
                color: '#666',
                whiteSpace: 'nowrap'
              }}>
                {t('sort_by') || 'Sort'}:
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: isMobile ? '8px 10px' : '6px 10px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: isMobile ? '13px' : '13px',
                  fontFamily: 'OpenSauceSans',
                  fontWeight: '500',
                  color: '#1a1a1a',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="date_desc">{t('sort_date_newest') || 'Date (Newest)'}</option>
                <option value="date_asc">{t('sort_date_oldest') || 'Date (Oldest)'}</option>
                <option value="name_asc">{t('sort_name_az') || 'Name (A-Z)'}</option>
                <option value="name_desc">{t('sort_name_za') || 'Name (Z-A)'}</option>
                <option value="group">{t('sort_by_group') || 'By Group'}</option>
              </select>
            </div>
          </div>
        )}

        {/* Articles List */}
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: isMobile ? '30px 0' : '40px 0',
            color: '#666',
            fontSize: isMobile ? '14px' : '15px',
            fontFamily: 'OpenSauceSans'
          }}>
            {t('loading_articles')}
          </div>
        ) : error ? (
          <div style={{
            textAlign: 'center',
            padding: isMobile ? '30px 0' : '40px 0',
            color: '#e74c3c',
            fontSize: isMobile ? '14px' : '15px',
            fontFamily: 'OpenSauceSans'
          }}>
            {t('error_loading_articles')}
          </div>
        ) : articles.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: isMobile ? '30px 0' : '40px 0',
            color: '#666',
            fontSize: isMobile ? '14px' : '15px',
            fontFamily: 'OpenSauceSans'
          }}>
            {t('empty_articles_message')}
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '6px' : '4px',
            maxWidth: isMobile ? '100%' : '1000px'
          }}>
            {articles
              .filter(article => {
                // Type filter
                if (articleTypeFilter !== 'both' && article.article_type !== articleTypeFilter) return false;
                // Group filter
                if (groupFilter === 'none') return !article.community_id;
                if (groupFilter !== 'all') return article.community_id === groupFilter;
                return true;
              })
              .sort((a, b) => {
                switch (sortBy) {
                  case 'date_asc':
                    return new Date(a.created_at) - new Date(b.created_at);
                  case 'name_asc':
                    return (a.title || '').localeCompare(b.title || '');
                  case 'name_desc':
                    return (b.title || '').localeCompare(a.title || '');
                  case 'group':
                    return (groupNames[a.community_id] || '').localeCompare(groupNames[b.community_id] || '');
                  case 'date_desc':
                  default:
                    return new Date(b.created_at) - new Date(a.created_at);
                }
              })
              .map((article) => (
              <div
                key={article.id}
                style={{
                  background: 'white',
                  borderRadius: '6px',
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  transition: 'box-shadow 0.2s ease',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'}
              >
                {/* Mobile Layout */}
                {isMobile ? (
                  <>
                    {/* Top row: Type indicator + Title + Edit button */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px'
                    }}>
                      {/* Type badge */}
                      <div
                        onClick={() => handleViewArticle(article)}
                        style={{
                          padding: '3px 6px',
                          background: article.article_type === 'event' ? '#BB4D9A' : '#024B99',
                          borderRadius: '3px',
                          fontSize: '9px',
                          fontWeight: '700',
                          color: 'white',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          flexShrink: 0,
                          cursor: 'pointer'
                        }}
                      >
                        {article.article_type === 'event' ? t('badge_event') : t('badge_news')}
                      </div>

                      {/* Title */}
                      <h3 style={{
                        margin: 0,
                        flex: 1,
                        minWidth: 0,
                        fontSize: '14px',
                        fontFamily: 'OpenSauceSans',
                        fontWeight: '600',
                        color: '#1a1a1a',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {article.title}
                      </h3>

                      {/* Edit button */}
                      <button
                        onClick={() => handleEditArticle(article)}
                        style={{
                          width: '32px',
                          height: '32px',
                          background: '#5BC0BE',
                          borderRadius: '50%',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <Edit2 size={16} color="white" />
                      </button>

                      {/* Hide button - only for published articles */}
                      {article.is_published && (
                        <button
                          onClick={() => handleHideArticle(article)}
                          style={{
                            width: '32px',
                            height: '32px',
                            background: '#f59e0b',
                            borderRadius: '50%',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <EyeOff size={16} color="white" />
                        </button>
                      )}

                      {/* Delete button */}
                      <button
                        onClick={() => handleDeleteArticle(article)}
                        style={{
                          width: '32px',
                          height: '32px',
                          background: '#ef4444',
                          borderRadius: '50%',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <Trash2 size={16} color="white" />
                      </button>
                    </div>

                    {/* Bottom row: Stats */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-around',
                      padding: '6px 8px',
                      borderTop: '1px solid #f0f0f0',
                      background: '#fafafa'
                    }}>
                      <div style={{ textAlign: 'center', flex: 1 }}>
                        <div style={{ fontSize: '9px', color: '#666', fontFamily: 'OpenSauceSans' }}>{t('stat_group') || 'Group'}</div>
                        <div style={{
                          fontSize: '11px',
                          color: '#1a1a1a',
                          fontFamily: 'OpenSauceSans',
                          fontWeight: '600',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {groupNames[article.community_id] || '—'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '9px', color: '#666', fontFamily: 'OpenSauceSans' }}>{t('stat_status')}</div>
                        <div style={{
                          fontSize: '11px',
                          color: article.is_published ? '#1a1a1a' : '#ef4444',
                          fontFamily: 'OpenSauceSans',
                          fontWeight: '600'
                        }}>
                          {article.is_published ? t('status_published') : t('status_draft')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '9px', color: '#666', fontFamily: 'OpenSauceSans' }}>{t('stat_date')}</div>
                        <div style={{ fontSize: '11px', color: '#1a1a1a', fontFamily: 'OpenSauceSans', fontWeight: '600' }}>
                          {formatDate(article.published_at)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '9px', color: '#666', fontFamily: 'OpenSauceSans' }}>{t('stat_views')}</div>
                        <div style={{ fontSize: '11px', color: '#1a1a1a', fontFamily: 'OpenSauceSans', fontWeight: '600' }}>
                          {viewCounts[article.id] ?? article.view_count ?? 0}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Desktop Layout - Horizontal badge */
                  <>
                    {/* Type badge - horizontal */}
                    <div
                      onClick={() => handleViewArticle(article)}
                      style={{
                        padding: '4px 10px',
                        marginLeft: '8px',
                        background: article.article_type === 'event' ? '#BB4D9A' : '#024B99',
                        fontSize: '11px',
                        fontFamily: 'OpenSauceSans',
                        fontWeight: '700',
                        color: 'white',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '52px',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.85'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      {article.article_type === 'event' ? t('badge_event') : t('badge_news')}
                    </div>

                    {/* Main Content Area */}
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      padding: '6px 10px',
                      gap: '16px'
                    }}>
                      {/* Article Title */}
                      <h3 style={{
                        margin: 0,
                        flex: 1,
                        minWidth: 0,
                        fontSize: '14px',
                        fontFamily: 'OpenSauceSans',
                        fontWeight: '600',
                        color: '#1a1a1a',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {article.title}
                      </h3>

                      {/* Stats - compact */}
                      <div style={{
                        display: 'flex',
                        gap: '20px',
                        alignItems: 'center',
                        flexShrink: 0
                      }}>
                        <div style={{ textAlign: 'center', minWidth: '80px', maxWidth: '140px' }}>
                          <div style={{ fontSize: '10px', color: '#666', fontFamily: 'OpenSauceSans' }}>{t('stat_group') || 'Group'}</div>
                          <div style={{
                            fontSize: '12px',
                            color: '#1a1a1a',
                            fontFamily: 'OpenSauceSans',
                            fontWeight: '600',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {groupNames[article.community_id] || '—'}
                          </div>
                        </div>

                        <div style={{ textAlign: 'center', minWidth: '55px' }}>
                          <div style={{ fontSize: '10px', color: '#666', fontFamily: 'OpenSauceSans' }}>{t('stat_status')}</div>
                          <div style={{
                            fontSize: '12px',
                            color: article.is_published ? '#1a1a1a' : '#ef4444',
                            fontFamily: 'OpenSauceSans',
                            fontWeight: '600'
                          }}>
                            {article.is_published ? t('status_published') : t('status_draft')}
                          </div>
                        </div>

                        <div style={{ textAlign: 'center', minWidth: '55px' }}>
                          <div style={{ fontSize: '10px', color: '#666', fontFamily: 'OpenSauceSans' }}>{t('stat_date')}</div>
                          <div style={{ fontSize: '12px', color: '#1a1a1a', fontFamily: 'OpenSauceSans', fontWeight: '600' }}>
                            {formatDate(article.published_at)}
                          </div>
                        </div>

                        <div style={{ textAlign: 'center', minWidth: '40px' }}>
                          <div style={{ fontSize: '10px', color: '#666', fontFamily: 'OpenSauceSans' }}>{t('stat_views')}</div>
                          <div style={{ fontSize: '12px', color: '#1a1a1a', fontFamily: 'OpenSauceSans', fontWeight: '600' }}>
                            {viewCounts[article.id] ?? article.view_count ?? 0}
                          </div>
                        </div>

                        <div style={{ textAlign: 'center', minWidth: '40px' }}>
                          <div style={{ fontSize: '10px', color: '#666', fontFamily: 'OpenSauceSans' }}>{t('stat_rating')}</div>
                          <div style={{ fontSize: '12px', color: '#1a1a1a', fontFamily: 'OpenSauceSans', fontWeight: '600' }}>
                            {article.rating ? article.rating.toFixed(1) : '0.0'}
                          </div>
                        </div>
                      </div>

                      {/* Edit Button */}
                      <button
                        onClick={() => handleEditArticle(article)}
                        style={{
                          width: '36px',
                          height: '36px',
                          background: '#5BC0BE',
                          borderRadius: '50%',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#4AA9A7';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#5BC0BE';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <Edit2 size={18} color="white" />
                      </button>

                      {/* Hide Button - only for published articles */}
                      {article.is_published && (
                        <button
                          onClick={() => handleHideArticle(article)}
                          style={{
                            width: '36px',
                            height: '36px',
                            background: '#f59e0b',
                            borderRadius: '50%',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            flexShrink: 0
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#d97706';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#f59e0b';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <EyeOff size={18} color="white" />
                        </button>
                      )}

                      {/* Delete Button */}
                      <button
                        onClick={() => handleDeleteArticle(article)}
                        style={{
                          width: '36px',
                          height: '36px',
                          background: '#ef4444',
                          borderRadius: '50%',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#dc2626';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#ef4444';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <Trash2 size={18} color="white" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Mobile Go to Top Button */}
        {isMobile && showGoToTop && (
          <button
            onClick={scrollToTop}
            style={{
              position: 'fixed',
              bottom: '20px',
              right: '16px',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: 'rgba(2, 75, 153, 0.9)',
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100
            }}
          >
            <ChevronUp size={24} color="white" />
          </button>
        )}
      </div>

      {/* Footer - Desktop only */}
      {!isMobile && (
        <FooterContainer>
          <FooterButtonsList />
        </FooterContainer>
      )}

      {/* Hide Confirmation Modal */}
      <ConfirmationModal
        visible={hideModal.visible}
        title={t('hide_article_title')}
        message={t('hide_article_confirm', { title: hideModal.article?.title || '' })}
        onOk={confirmHide}
        onCancel={cancelHide}
        okLabel={hiding ? t('hiding') : t('hide')}
        cancelLabel={t('cancel')}
        danger={true}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={deleteModal.visible}
        title={t('delete_article_title')}
        message={
          (deleteModal.commentCount || 0) === 1
            ? t('delete_article_with_one_comment_confirm')
            : (deleteModal.commentCount || 0) > 1
              ? t('delete_article_with_comments_confirm', { count: deleteModal.commentCount })
              : t('delete_article_confirm', { title: deleteModal.article?.title || '' })
        }
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okLabel={deleting ? t('deleting') : t('delete')}
        cancelLabel={t('cancel')}
        danger={true}
      />

      <ConfirmationModal
        visible={infoModal.visible}
        message={infoModal.message}
        onOk={() => setInfoModal({ visible: false, message: '' })}
        okLabel="OK"
      />

      {/* Edit window: the shared full-screen overlay, below the modal tier, so every message the
          form raises shows in front of it (#78, addendum 1 item 1). */}
      {editModal.visible && editModal.article && (
        <FullScreenOverlay onClose={handleEditModalClose} panelBackground="#f3f4f6">
            {editModal.article.article_type === 'event' ? (
              <AddEditEvent
                userId={userId}
                onBack={handleEditModalClose}
                currentContext={currentContext}
                mode="edit"
                event={editModal.article}
              />
            ) : (
              <AddEditArticle
                userId={userId}
                onBack={handleEditModalClose}
                currentContext={currentContext}
                mode="edit"
                article={editModal.article}
              />
            )}
        </FullScreenOverlay>
      )}
    </div>
  );
};

export default MyArticles;
